import { useFormikContext } from 'formik';
import { Dropdown, ErrorContainer, LabelComponent } from 'components';
import { PERMISSION } from 'utils/protectedRoutes/userRolesAndPermissionTypes';
import { useIsOnlyVisibleToUserWithPermissions } from 'utils/hooks/useIsOnlyVisibleTo';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getLsUserRoleObject } from 'modules/networkTools/localStorageTokens';
import { useEffect } from 'react';
import { getClientsStatuses } from 'store/clients/clientsActions';
import { setChangeStatusModal } from 'store/clients/clientsSlice';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  HowDidYouReachThemResetFields,
  WhoWereYouTryingToReachResetFields
} from '../whoDidYouReach/whoDidYouReach';
import { WhichProviderResetFields } from '../whoDidYouReach/whoDidYouReach';
import { useNoteFormContext } from '../../context';
import style from './noteType.module.scss';

interface NoteTypeProps {
  disabled: boolean;
}

export const getNoteTypeData = ({
  canUpdateManagerReview
}: {
  canUpdateManagerReview: boolean;
}) => [
  { id: 'CHART_REVIEW', name: 'Chart Audit', show: true },
  { id: 'END_OF_LIFE', name: 'End of Life', show: true },
  { id: 'FOLLOW_UP', name: 'Follow-up', show: true },
  { id: 'GRADUATION', name: 'Graduation', show: true },
  { id: 'HEALTH_GOAL', name: 'Health Goal', show: true },
  { id: 'HOSPITALIZATION', name: 'Hospitalization', show: true },
  { id: 'MANAGER_REVIEW', name: 'Manager Review', show: canUpdateManagerReview },
  { id: 'MEET_THE_PERSON_INTERVIEW', name: 'Meet the Person Interview', show: true },
  { id: 'MEET_THE_PERSON_INTERVIEW_CHILD', name: 'Meet the Person Interview - Child', show: true },
  { id: 'MEET_THE_PERSON_INTERVIEW_TEEN', name: 'Meet the Person Interview - Teen', show: true },
  { id: 'MID_PREGNANCY_FOLLOW_UP', name: 'Mid-Pregnancy Follow-up', show: true },
  { id: 'OUTREACH', name: 'Outreach', show: true },
  { id: 'OUTREACH_CHILD', name: 'Outreach - Child', show: true },
  { id: 'OUTREACH_TEEN', name: 'Outreach - Teen', show: true },
  { id: 'POST_DELIVERY', name: 'Post-Delivery', show: true },
  { id: 'PROVIDER_APPOINTMENT', name: 'Provider Appointment', show: true },
  { id: 'SAFETY_NOTE', name: 'Safety Note', show: true },
  { id: 'STAFFING_NOTE', name: 'Staffing Note', show: true }
];

export const NoteType = ({ disabled }: NoteTypeProps) => {
  const { enableMpiUpdate } = useFlags();
  const statusesList = useAppSelector((state) => state.clients.statusesList);
  const dispatch = useAppDispatch();
  const canUpdateManagerReview = useIsOnlyVisibleToUserWithPermissions([
    PERMISSION.UPDATE_MANAGER_REVIEW
  ]);
  const formik = useFormikContext<{
    noteType: string | null;
    note: string;
  }>();
  const { status } = useNoteFormContext();

  const noteTypeData = getNoteTypeData({
    canUpdateManagerReview: disabled && !canUpdateManagerReview ? true : canUpdateManagerReview
  }).filter((n) => n.show);

  useEffect(() => {
    dispatch(getClientsStatuses());
  }, []);

  const chwId = useAppSelector((state) => state.clients.client?.data?.chwId);

  const managers = useAppSelector((state) => state.clients.managers.data);
  const userRole = getLsUserRoleObject();
  const userId = userRole.userId;
  const currentManager = managers.find((manager) => manager.id.toString() === userId.toString());

  const newNoteTypes =
    statusesList
      .find((s) => s.id === status)
      ?.noteConfig?.availableNoteTypes?.map((n) => ({
        id: n.id,
        name: n.displayName
      })) ?? [];

  const showWantToDoAnMPI = [
    'PRE_OUTREACH',
    'PENDING',
    'NOT_REACHED',
    'DECLINED',
    'OPT_OUT',
    'INELIGIBLE'
  ].includes(status);

  return (
    <>
      <LabelComponent mandatory text="Note Type" />
      <Dropdown
        smallDropdown
        withGradient
        label="Select"
        data={enableMpiUpdate ? newNoteTypes : noteTypeData}
        onChange={(e) => {
          formik.handleChange(e);

          if (e.target.value === 'MANAGER_REVIEW') {
            [
              { key: 'startTimeOfEncounter', value: null },
              { key: 'nextContact', value: null },
              { key: 'whoDidYouReach', value: [] },
              { key: 'typeOfSupport', value: [] },
              { key: 'zCodes', value: [] },
              ...WhoWereYouTryingToReachResetFields,
              ...HowDidYouReachThemResetFields,
              ...WhichProviderResetFields
            ].forEach((field) => {
              formik.setFieldValue(field.key, field.value);
              formik.setFieldTouched(field.key, false);
            });
            const managerReviewNotePreset =
              "CHW reviewed client's applicable history, changes since last staffing, goals, and interventions to be used to help the client achieve next steps with supervisor.";
            formik.setFieldValue('timeSpentMin', '10');
            formik.setFieldValue('chwId', chwId?.toString() || null);
            formik.setFieldValue('managerId', currentManager?.id.toString() || null);
            formik.setFieldValue(
              'note',
              formik.values.note && formik.values.note?.length > 0
                ? `${formik.values.note} ${managerReviewNotePreset}`
                : managerReviewNotePreset
            );
            formik.setFieldValue('documentationTime', '1');
          }
          if (formik.values.noteType === 'MANAGER_REVIEW' && e.target.value !== 'MANAGER_REVIEW') {
            formik.setFieldValue('timeSpentMin', '');
          }
        }}
        onBlur={formik.handleBlur}
        name="noteType"
        id="noteType"
        value={formik.values.noteType ?? ''}
        disabled={disabled}
      >
        {showWantToDoAnMPI && enableMpiUpdate && (
          <button
            className={style.wantToDoAnMPI}
            onClick={() => {
              dispatch(setChangeStatusModal('changeStatus'));
            }}
          >
            Want to do an MPI? Change your client's status to Enrolled.
          </button>
        )}
      </Dropdown>
      <ErrorContainer
        visible={formik.touched.noteType && formik.errors.noteType}
        errors={formik.errors}
        name="noteType"
      />
    </>
  );
};
